import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { EDestinationType } from '@/core/interfaces';
import { GtagTransformer } from '@/destinations/shared/gtag/gtag_transformer';
import { GtagEvent } from '@/destinations/shared/gtag/gtag_transformer/types';
import { GTMWrapper } from '@/destinations/shared/gtag/tag_manager_wrapper/gtm_wrapper';
import { processCustomerData } from '@/destinations/shared/gtag/tag_manager_wrapper/user_data_helper';
import { TGTMDestinationCredentials } from '@/destinations/types/destination_creds_types';
import { Logger } from '@/shared/logger/logger';

import {
  DestinationBundle,
  DestinationPayload,
  DestinationSendingResult,
} from '../abstract';

export class GTMDestinationBundle extends DestinationBundle {
  private readonly gtm: GTMWrapper;
  private readonly gtmId: string;
  override allowedEvents: EMableEventName[] = [
    EMableEventName.PAGE_VIEWED,
    EMableEventName.PRODUCT_ADDED,
    EMableEventName.CHECKOUT_STARTED,
    EMableEventName.PAYMENT_INFO_ADDED,
    EMableEventName.PRODUCT_VIEWED,
    EMableEventName.LEAD,
    // EMableEventName.COLLECTION_VIEWED,
    EMableEventName.ORDER_COMPLETED,
    EMableEventName.CUSTOM_EVENT,
  ];
  override destinationConfig: TGTMDestinationCredentials;
  constructor(
    destinationSetting: TGTMDestinationCredentials,
    destinationId: string,
  ) {
    super(EDestinationType.GTM, destinationSetting, destinationId);
    this.destinationConfig = destinationSetting;
    if (
      !this.destinationConfig?.containerId &&
      !(this.destinationConfig as any)?.config?.containerId
    ) {
      throw new Error(
        'GTMDestinationBundle: Missing required configuration parameter: containerId',
      );
    }

    this.gtm = new GTMWrapper();
    this.gtmId = this.destinationConfig.containerId;
  }

  override async init(): Promise<void> {
    try {
      this.gtm.initGTM(this.gtmId);
    } catch (error) {
      Logger.error(
        'GTMDestinationBundle: Error initializing GTM destination',
        error,
      );
    }
  }

  override async calculatePayload(
    mableEvent: TMableEvent,
  ): Promise<DestinationPayload> {
    const ga4Event = GtagTransformer.transform(
      mableEvent,
      this.destinationConfig,
    );
    const userData = await processCustomerData(mableEvent.cdas);

    return {
      ...ga4Event,
      user_data: userData,
    };
  }

  override async sendToDestination(
    payload: GtagEvent & { user_data: any },
  ): Promise<DestinationSendingResult> {
    try {
      // Set user properties
      this.gtm.setUserProperties(payload.user_data);

      // Track the event
      this.gtm.trackEvent(payload.event_name, '', payload.event_params);

      if (payload.event_params.user_id) {
        this.gtm.setUserId(payload.event_params.user_id);
      }

      return {
        destinationReqEvent: payload,
        reason: 'Payload successfully sent to GTM',
        success: true,
      };
    } catch (error) {
      return {
        destinationReqEvent: payload,
        reason:
          error instanceof Error ? error.message : 'Unknown error occurred',
        success: false,
      };
    }
  }
}
